import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AssessmentResults = ({ results, onRestart, onGetQuote }) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareEmail, setShareEmail] = useState('');

  const handleShare = () => {
    // Mock share functionality
    console.log('Sharing results to:', shareEmail);
    setShowShareModal(false);
    setShareEmail('');
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-error';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-success/10 border-success/20';
    if (score >= 60) return 'bg-warning/10 border-warning/20';
    return 'bg-error/10 border-error/20';
  };

  return (
    <div className="space-y-6">
      {/* Overall Score */}
      <div className="bg-white rounded-lg shadow-brand-moderate p-8 text-center">
        <div className="mb-6">
          <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full border-4 ${getScoreBgColor(results?.overallScore)}`}>
            <span className={`text-3xl font-bold ${getScoreColor(results?.overallScore)}`}>
              {results?.overallScore}
            </span>
          </div>
          <h2 className="text-2xl font-semibold text-text-primary mt-4 mb-2">
            Network Assessment Complete
          </h2>
          <p className="text-text-secondary">
            {results?.overallScore >= 80 ? 'Your network is performing well' :
             results?.overallScore >= 60 ? 'Your network needs optimization': 'Your network requires immediate attention'}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="text-center">
            <div className={`text-2xl font-bold ${getScoreColor(results?.performanceScore)}`}>
              {results?.performanceScore}%
            </div>
            <div className="text-sm text-text-secondary">Performance</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${getScoreColor(results?.reliabilityScore)}`}>
              {results?.reliabilityScore}%
            </div>
            <div className="text-sm text-text-secondary">Reliability</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${getScoreColor(results?.futureReadyScore)}`}>
              {results?.futureReadyScore}%
            </div>
            <div className="text-sm text-text-secondary">Future-Ready</div>
          </div>
        </div>
      </div>
      {/* Key Findings */}
      <div className="bg-white rounded-lg shadow-brand-moderate p-6">
        <h3 className="text-xl font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Search" size={20} className="mr-2" />
          Key Findings
        </h3>
        <div className="space-y-4">
          {results?.findings?.map((finding, index) => (
            <div key={index} className="flex items-start space-x-3 p-4 bg-surface rounded-lg">
              <Icon 
                name={finding?.type === 'critical' ? 'AlertTriangle' : 
                      finding?.type === 'warning' ? 'AlertCircle' : 'CheckCircle'} 
                size={20} 
                className={finding?.type === 'critical' ? 'text-error' : 
                          finding?.type === 'warning' ? 'text-warning' : 'text-success'}
              />
              <div className="flex-1">
                <h4 className="font-medium text-text-primary">{finding?.title}</h4>
                <p className="text-sm text-text-secondary mt-1">{finding?.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Recommendations */}
      <div className="bg-white rounded-lg shadow-brand-moderate p-6">
        <h3 className="text-xl font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Lightbulb" size={20} className="mr-2" />
          Recommended Solutions
        </h3>
        <div className="space-y-4">
          {results?.recommendations?.map((rec, index) => (
            <div key={index} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-semibold text-text-primary">{rec?.title}</h4>
                  <p className="text-sm text-text-secondary mt-1">{rec?.description}</p>
                </div>
                <div className="text-right ml-4">
                  <div className="text-lg font-semibold text-primary">{rec?.estimatedCost}</div>
                  <div className="text-xs text-text-secondary">Estimated Cost</div>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-4">
                  <span className="text-text-secondary">Timeline: {rec?.timeline}</span>
                  <span className="text-text-secondary">ROI: {rec?.roi}</span>
                </div>
                <div className={`px-2 py-1 rounded text-xs font-medium ${
                  rec?.priority === 'High' ? 'bg-error/10 text-error' :
                  rec?.priority === 'Medium'? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'
                }`}>
                  {rec?.priority} Priority
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* ROI Projection */}
      <div className="bg-white rounded-lg shadow-brand-moderate p-6">
        <h3 className="text-xl font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="TrendingUp" size={20} className="mr-2" />
          Projected ROI from Fiber Conversion
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-surface rounded-lg">
            <div className="text-2xl font-bold text-success">{results?.roi?.productivity}</div>
            <div className="text-sm text-text-secondary">Productivity Increase</div>
          </div>
          <div className="text-center p-4 bg-surface rounded-lg">
            <div className="text-2xl font-bold text-primary">{results?.roi?.costSavings}</div>
            <div className="text-sm text-text-secondary">Annual Cost Savings</div>
          </div>
          <div className="text-center p-4 bg-surface rounded-lg">
            <div className="text-2xl font-bold text-accent">{results?.roi?.paybackPeriod}</div>
            <div className="text-sm text-text-secondary">Payback Period</div>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="bg-white rounded-lg shadow-brand-moderate p-6">
        <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0 sm:space-x-4">
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={onRestart}
              iconName="RotateCcw"
              iconPosition="left"
            >
              Retake Assessment
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowShareModal(true)}
              iconName="Share2"
              iconPosition="left"
            >
              Share Results
            </Button>
          </div>
          
          <div className="flex space-x-3">
            <Button
              variant="secondary"
              iconName="Download"
              iconPosition="left"
            >
              Download Report
            </Button>
            <Button
              variant="default"
              onClick={onGetQuote}
              iconName="MessageSquare"
              iconPosition="right"
              className="cta-primary"
            >
              Get Detailed Quote
            </Button>
          </div>
        </div>
      </div>
      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-brand-strong max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-text-primary">Share Assessment Results</h3>
                <Button variant="ghost" size="icon" onClick={() => setShowShareModal(false)}>
                  <Icon name="X" size={20} />
                </Button>
              </div>
              
              <Input
                label="Email Address"
                type="email"
                value={shareEmail}
                onChange={(e) => setShareEmail(e?.target?.value)}
                placeholder="colleague@company.com"
                className="mb-4"
              />
              
              <div className="flex justify-end space-x-3">
                <Button variant="outline" onClick={() => setShowShareModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleShare}
                  disabled={!shareEmail}
                  iconName="Send"
                  iconPosition="right"
                >
                  Send Results
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentResults;